import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import type { GlideCandidate, GlidePoint, KeyBox } from "./types";
import { getGlideDictSync, warmGlideDict } from "./dict";
import { decodeGlide, rawKeySequence } from "./decoder";

/**
 * useGlide — жест росчерка на зоне клавиш TextPad (06.09, владелец: ввод
 * росчерком, как Gboard). Тап остаётся тапом: буква вставляется на pointerdown
 * кнопки как раньше (латентность не трогаем). Если палец после касания
 * [data-key] уезжает дальше 0.6 ширины клавиши (и жест длится ≥ 80 мс) —
 * режим glide: зона захватывает указатель, под пальцем рисуется след (SVG
 * polyline, императивно — без ре-рендеров на каждый pointermove), на
 * отпускании путь + геометрия клавиш + словарь → кандидаты слов хосту.
 *
 * Слушатели висят на document (pointerdown — capture): зона шита монтируется
 * на кадр позже open (SheetDialog: setShown в эффекте), а ref читается в
 * момент события. Capture-фаза = раньше React-обработчика кнопки, поэтому
 * start = КАРЕТКА до вставки первой буквы, а shift ещё не сброшен.
 * 06.09 (владелец): каретка вместо длины — росчерк в середине текста
 * подставляет слово на место тапнутой буквы, хвост после неё сохраняется.
 */
export interface GlideResult {
  /** Каретка ДО вставки первой буквы: с этого индекса слово заменяет значение. */
  start: number;
  /** Символ первой клавиши (data-key, нижний регистр) — хост проверяет, вошла ли буква (maxLength). */
  pressed: string;
  candidates: GlideCandidate[];
  /** Shift был включён на старте — первая буква слова в верхнем регистре. */
  upper: boolean;
}

export interface UseGlideOpts {
  enabled: boolean;
  /** Каретка хоста в момент pointerdown (до вставки буквы). */
  getCaret: () => number;
  getUpper: () => boolean;
  onGlide: (r: GlideResult) => void;
  /** CSS-класс SVG-следа (.trail) и класс зоны на время росчерка (.gliding). */
  trailClass?: string;
  glideClass?: string;
}

const MOVE_K = 0.6;   /* порог росчерка — доля ширины стартовой клавиши */
const MIN_MS = 80;    /* короче — тап, даже если палец дёрнулся */
const FADE_MS = 250;  /* след гаснет после отпускания */
const LIMIT = 4;

interface Gesture {
  id: number; t0: number; x0: number; y0: number; w: number;
  start: number; pressed: string; upper: boolean; pts: GlidePoint[]; active: boolean;
}

/** Прямоугольники всех [data-key] зоны в её координатах. */
function collectKeys(zone: HTMLElement): KeyBox[] {
  const zr = zone.getBoundingClientRect();
  return Array.from(zone.querySelectorAll<HTMLElement>("[data-key]")).map((el) => {
    const r = el.getBoundingClientRect();
    return { ch: el.dataset.key ?? "", cx: r.left - zr.left + r.width / 2, cy: r.top - zr.top + r.height / 2, w: r.width, h: r.height };
  });
}

function decode(path: GlidePoint[], keys: KeyBox[]): GlideCandidate[] {
  const dict = getGlideDictSync();
  let cands: GlideCandidate[] = [];
  if (dict) cands = decodeGlide(path, keys, dict, LIMIT);
  else warmGlideDict();
  if (!cands.length) {
    const raw = rawKeySequence(path, keys);
    if (raw) cands = [{ word: raw, score: 0 }];
  }
  return cands;
}

export function useGlide(zoneRef: RefObject<HTMLDivElement | null>, opts: UseGlideOpts): void {
  const o = useRef(opts);
  o.current = opts;
  const { enabled } = opts;

  useEffect(() => {
    if (!enabled) return;
    let g: Gesture | null = null;
    let svg: SVGSVGElement | null = null;
    let line: SVGPolylineElement | null = null;
    let fadeT = 0;
    const NS = "http://www.w3.org/2000/svg";

    const trail = (zone: HTMLElement): SVGPolylineElement => {
      if (svg && line && svg.parentNode === zone) return line;
      svg?.remove();
      svg = document.createElementNS(NS, "svg");
      if (o.current.trailClass) svg.setAttribute("class", o.current.trailClass);
      svg.setAttribute("aria-hidden", "true");
      line = document.createElementNS(NS, "polyline");
      svg.appendChild(line);
      zone.appendChild(svg);
      return line;
    };
    const draw = (pts: GlidePoint[]) => {
      if (!line) return;
      line.setAttribute("points", pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" "));
    };
    const show = () => {
      if (!svg) return;
      window.clearTimeout(fadeT);
      svg.style.transition = "none";
      svg.style.opacity = "0.7";
    };
    const fade = () => {
      if (!svg) return;
      svg.style.transition = `opacity ${FADE_MS}ms`;
      svg.style.opacity = "0";
      fadeT = window.setTimeout(() => line?.setAttribute("points", ""), FADE_MS);
    };

    /* t: у коалесцированных событий timeStamp в части WebKit = 0 — берём время родителя. */
    const rel = (e: PointerEvent, zone: HTMLElement, t = e.timeStamp): GlidePoint => {
      const r = zone.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top, t: e.timeStamp || t };
    };

    const down = (e: PointerEvent) => {
      const zone = zoneRef.current;
      if (!zone || !e.isPrimary || g) return;
      const key = (e.target as Element | null)?.closest?.("[data-key]");
      if (!key || !zone.contains(key)) return;
      const p = rel(e, zone);
      g = {
        id: e.pointerId, t0: p.t ?? e.timeStamp, x0: p.x, y0: p.y,
        w: key.getBoundingClientRect().width || 40,
        start: o.current.getCaret(), pressed: ((key as HTMLElement).dataset.key ?? "").toLowerCase(),
        upper: o.current.getUpper(), pts: [p], active: false,
      };
    };

    const move = (e: PointerEvent) => {
      const zone = zoneRef.current;
      if (!g || !zone || e.pointerId !== g.id) return;
      const raw = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [];
      for (const ce of raw.length ? raw : [e]) g.pts.push(rel(ce, zone, e.timeStamp));
      const last = g.pts[g.pts.length - 1];
      if (!last) return;
      if (!g.active) {
        const d = Math.hypot(last.x - g.x0, last.y - g.y0);
        if (d <= MOVE_K * g.w || e.timeStamp - g.t0 < MIN_MS) return;
        g.active = true;
        try { zone.setPointerCapture(e.pointerId); } catch { /* указатель уже отпущен */ }
        if (o.current.glideClass) zone.classList.add(o.current.glideClass);
        trail(zone);
        show();
      }
      draw(g.pts);
    };

    const finish = (e: PointerEvent, cancel: boolean) => {
      const zone = zoneRef.current;
      if (!g || e.pointerId !== g.id) return;
      const st = g;
      g = null;
      if (!st.active || !zone) return;
      if (o.current.glideClass) zone.classList.remove(o.current.glideClass);
      try { zone.releasePointerCapture(e.pointerId); } catch { /* уже отпущен */ }
      fade();
      if (cancel) return;
      st.pts.push(rel(e, zone));
      const cands = decode(st.pts, collectKeys(zone));
      if (cands.length) o.current.onGlide({ start: st.start, pressed: st.pressed, candidates: cands, upper: st.upper });
    };
    const up = (e: PointerEvent) => finish(e, false);
    const cancel = (e: PointerEvent) => finish(e, true);

    document.addEventListener("pointerdown", down, true);
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("pointercancel", cancel);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.removeEventListener("pointercancel", cancel);
      window.clearTimeout(fadeT);
      svg?.remove();
    };
  }, [enabled, zoneRef]);
}
